import React, { useState, useMemo } from "react";
import { Lead } from "../../types";
import { Search, Headphones, RefreshCw } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { API_BASE_URL } from "../../utils/apiConfig";
import MyLeadsTable from "./MyLeadsTable";
import LeadDetailsView from "./LeadDetailsView";

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  countries?: string[];
  alterCPAUserId?: number;
}

interface MyLeadsProps {
  orders: Lead[]; // Changed from Order[] to Lead[] - keeping prop name for compatibility
  totalCount: number;
  onRefresh: () => void;
  refreshing?: boolean;
  onFetchLeads: (page: number, limit: number, search: string, statusFilter: string) => Promise<void>;
  currentUser: User;
  onEditLead?: (leadId: string) => void; // Changed to accept leadId (string)
  onViewLead?: (lead: Lead) => void;
}

const MyLeads: React.FC<MyLeadsProps> = ({
  orders,
  totalCount,
  onRefresh,
  refreshing = false,
  onFetchLeads,
  currentUser,
  onEditLead,
  onViewLead,
}) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedLeadForView, setSelectedLeadForView] = useState<Lead | null>(null);
  const [showLeadDetails, setShowLeadDetails] = useState(false);

  // Client-side search: Customer Name and Phone only. Phone normalized so "0" prefix and 381/063 forms all match.
  const filteredOrders = useMemo(() => {
    if (!searchTerm.trim()) return orders;
    const s = searchTerm.trim();
    const sLower = s.toLowerCase();
    const searchDigits = s.replace(/\D/g, "");

    return orders.filter((lead) => {
      // Customer name
      const name = (lead.name ?? "").toLowerCase();
      const nameMatch = name.includes(sLower);

      // Phone: match against digits; handle leading 0 and 381 so "063", "63", "38163" all find the same lead
      const leadDigits = (lead.phone ?? "").replace(/\D/g, "");
      const leadWithLeadingZero = leadDigits.startsWith("381") ? "0" + leadDigits.substring(3) : leadDigits;
      const phoneMatch =
        searchDigits.length > 0 && (leadDigits.includes(searchDigits) || leadWithLeadingZero.includes(searchDigits));

      return nameMatch || phoneMatch;
    });
  }, [orders, searchTerm]);

  const handleEditLead = (lead: Lead) => {
    // Navigate to Lead Processing Page using lead.id (string)
    if (onEditLead && lead.id) {
      onEditLead(lead.id);
    }
  };

  const handleViewLead = (lead: Lead) => {
    setSelectedLeadForView(lead);
    setShowLeadDetails(true);
  };

  // Status options for filtering
  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "1", label: "New" },
    { value: "2", label: "Processing" },
    { value: "3", label: "Call back" },
    { value: "4", label: "Hold" },
    { value: "5", label: "Cancel" },
    { value: "6", label: "Packing" },
    { value: "7", label: "Sending" },
    { value: "8", label: "Delivery" },
    { value: "9", label: "Delivered" },
    { value: "10", label: "Complete" },
    { value: "11", label: "Return" },
    { value: "12", label: "Delete" },
  ];

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Headphones className="w-6 h-6 mr-2 text-green-600" />
              My Leads
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {searchTerm.trim()
                ? `${filteredOrders.length} of ${totalCount} lead${totalCount !== 1 ? "s" : ""}`
                : `${totalCount} lead${totalCount !== 1 ? "s" : ""} assigned to you`}
            </p>
          </div>
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] disabled:opacity-50 disabled:cursor-not-allowed"
            title={t("callCenter.refresh")}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {t("message.refresh")}
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by name, phone, email, or ID..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Status Filter - Hidden for now since we're showing leads, not orders */}
          {/* <div className="sm:w-48">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-5 w-5 text-gray-400" />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={loading}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div> */}
        </div>
      </div>

      {/* Table — filtered client-side so search input stays focused and typing feels natural */}
      <MyLeadsTable
        orders={filteredOrders}
        onEditLead={handleEditLead}
        onViewLead={onViewLead || handleViewLead}
        currentUser={currentUser}
      />

      {/* Lead Details View */}
      <LeadDetailsView
        isOpen={showLeadDetails}
        onClose={() => {
          setShowLeadDetails(false);
          setSelectedLeadForView(null);
        }}
        order={selectedLeadForView as any}
        currentUser={currentUser}
      />

      {/* Pagination removed — showing all leads */}
    </div>
  );
};

export default MyLeads;

